import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Badge, Table, Spinner, Alert } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiPower, FiX } from '../../utils/icons';
import api from '../../services/api';
import { notify } from '../../stores/useNotificationStore';

interface IFramework {
  id: number;
  nome: string;
  descricao?: string;
  empresa_id: number;
  ativo: boolean;
  criado_por?: number;
  created_at?: string;
}

const FrameworkListPage: React.FC = () => {
  const [frameworks, setFrameworks] = useState<IFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterNome, setFilterNome] = useState('');
  const [filterAtivo, setFilterAtivo] = useState<'todos' | 'ativo' | 'inativo'>('todos');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const res = await api.getFrameworks();
    if (res.success && Array.isArray(res.data)) {
      setFrameworks(res.data as IFramework[]);
    } else {
      setError('Erro ao carregar frameworks.');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (fw: IFramework) => {
    if (!window.confirm(`Excluir o framework "${fw.nome}"? Esta ação remove todas as associações.`)) return;
    const res = await api.deleteFramework(fw.id);
    if (res.success) {
      setFrameworks(prev => prev.filter(f => f.id !== fw.id));
    } else {
      notify.error('Erro ao excluir o framework.');
    }
  };

  const handleToggleAtivo = async (fw: IFramework) => {
    const acao = fw.ativo ? 'desativar' : 'reativar';
    if (!window.confirm(`Deseja ${acao} o framework "${fw.nome}"?`)) return;
    const res = await api.updateFramework(fw.id, {
      nome: fw.nome,
      descricao: fw.descricao || '',
      ativo: !fw.ativo,
    });
    if (res.success) {
      setFrameworks(prev => prev.map(f => f.id === fw.id ? { ...f, ativo: !fw.ativo } : f));
    } else {
      notify.error(`Erro ao ${acao} o framework.`);
    }
  };

  const filtered = frameworks.filter(fw => {
    const nomeOk = fw.nome.toLowerCase().includes(filterNome.toLowerCase());
    const ativoOk = filterAtivo === 'todos' || (filterAtivo === 'ativo' ? fw.ativo : !fw.ativo);
    return nomeOk && ativoOk;
  });

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="mb-0">Listagem de Frameworks</h2>
          <p className="text-muted mb-0">Frameworks de conformidade cadastrados.</p>
        </div>
        <Button variant="primary" onClick={() => navigate('/app/frameworks')}>
          <FiPlus /> Novo Framework
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      {/* Filtros */}
      <Form as={Row} className="g-3 mb-4 p-3 border rounded bg-light">
        <Col md={5}>
          <Form.Group controlId="filterNome">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              type="text"
              placeholder="Buscar por nome..."
              value={filterNome}
              onChange={e => setFilterNome(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="filterAtivo">
            <Form.Label>Status</Form.Label>
            <Form.Select value={filterAtivo} onChange={e => setFilterAtivo(e.target.value as any)}>
              <option value="todos">Todos</option>
              <option value="ativo">Somente Ativos</option>
              <option value="inativo">Somente Inativos</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button variant="outline-secondary" onClick={() => { setFilterNome(''); setFilterAtivo('todos'); }}>
            <FiX /> Limpar Filtros
          </Button>
        </Col>
      </Form>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : filtered.length === 0 ? (
        <Alert variant="info">Nenhum framework encontrado.</Alert>
      ) : (
        <Table hover responsive bordered>
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Nome</th>
              <th>Descrição</th>
              <th style={{ width: 100 }}>Status</th>
              <th className="text-end" style={{ width: 300 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(fw => (
              <tr key={fw.id} className={!fw.ativo ? 'text-muted' : ''}>
                <td>{fw.id}</td>
                <td><strong>{fw.nome}</strong></td>
                <td>
                  <small className="text-muted">{fw.descricao || '—'}</small>
                </td>
                <td>
                  <Badge bg={fw.ativo ? 'success' : 'secondary'}>
                    {fw.ativo ? 'Ativo' : 'Inativo'}
                  </Badge>
                </td>
                <td className="text-end text-nowrap">
                  <div className="d-inline-flex gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => navigate(`/app/frameworks/editar/${fw.id}`)}
                    >
                      <FiEdit2 /> Editar
                    </Button>
                    <Button
                      variant={fw.ativo ? 'outline-warning' : 'outline-success'}
                      size="sm"
                      onClick={() => handleToggleAtivo(fw)}
                    >
                      <FiPower /> {fw.ativo ? 'Desativar' : 'Reativar'}
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(fw)}
                    >
                      <FiTrash2 /> Excluir
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default FrameworkListPage;
